// Minimal hash router. Routes look like '#/wizard' or '#/dashboard/ottawa'.
// Returns [route, params, navigate] from useRoute().
import { useEffect, useState, useCallback } from 'react';

function parse(hash) {
  const raw = (hash || '').replace(/^#\/?/, '');
  const [pathPart, queryPart] = raw.split('?');
  const segs = pathPart.split('/').filter(Boolean);
  const name = segs[0] || 'home';
  const rest = segs.slice(1);
  const query = Object.fromEntries(new URLSearchParams(queryPart || ''));
  return { name, rest, query };
}

// OGC-531: a screen with unsaved edits can hold navigation. The guard gets
// the target hash and a `proceed` callback, and returns false to hold; it then
// asks the person and calls `proceed()` (after clearing itself) if they
// discard.
let navigationGuard = null;
export function setNavigationGuard(guard) {
  navigationGuard = typeof guard === 'function' ? guard : null;
}

// Replace the current history entry's hash and tell every hash listener, as
// a real navigation would.
export function replaceHash(target) {
  const oldURL = window.location.href;
  window.history.replaceState(window.history.state, '', target);
  window.dispatchEvent(new HashChangeEvent('hashchange', { oldURL, newURL: window.location.href }));
}

// Asks the guard before an action that leaves the current screen without a
// hash navigation of its own (e.g. a company switch). Returns false while
// held; `proceed` runs later if the person discards.
export function requestNavigation(target, proceed) {
  if (!navigationGuard) return true;
  return navigationGuard(target, proceed) !== false;
}

// Hash changes that bypass navigate() (browser Back/Forward, links, direct
// location.hash writes) are held too. This listener is added when the module
// loads, before any component's, so a held change is undone before the rest
// of the app sees it. Undoing replaces the entry the browser moved to with
// the address being left, and proceeding replaces it back, so Back, Forward
// and links leave the history as they would without the guard.
if (typeof window !== 'undefined') {
  window.addEventListener('hashchange', (event) => {
    if (!navigationGuard) return;
    const target = new URL(event.newURL).hash || '#/';
    if (navigationGuard(target, () => replaceHash(target)) !== false) return;
    event.stopImmediatePropagation();
    window.history.replaceState(window.history.state, '', new URL(event.oldURL).hash || '#/');
  });
}

export function useRoute() {
  const [route, setRoute] = useState(() => parse(window.location.hash));
  useEffect(() => {
    const onHash = () => setRoute(parse(window.location.hash));
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const navigate = useCallback((path, options = {}) => {
    const target = path.startsWith('#') ? path : `#/${path.replace(/^\//, '')}`;
    if (window.location.hash === target) return;
    if (navigationGuard && !options.force && navigationGuard(target, () => navigate(path, { ...options, force: true })) === false) return;
    if (options.replace) {
      window.history.replaceState(window.history.state, '', target);
      setRoute(parse(target));
      return;
    }
    window.location.hash = target;
  }, []);

  return [route, navigate];
}
